import { useState, useEffect } from 'react'
import { Sheet } from './UI'
import { ClapboardClosed, ClapboardOpen } from './ClapboardIcon'
import { ReelRating } from './ReelRating'
import { logEvent } from '../lib/analytics'

// ── EpisodeSheet17 ─────────────────────────────────────────────────────────────
// Tap an episode row on the show page → this. The one place to rate an episode
// (five reels, tap again to clear), keep a private note, and mark or rewind.
// Episode ratings and notes had no door at all in 17 before this (audit 2026-09-15):
// `rateEpisode` was only reachable from the deck's hold and the post-mark peek, and
// `saveEpNote` had zero callers.

const EYE = { fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--amber)' }

export function EpisodeSheet17({ show, season, episode, name, meta, watched, isNext, aired, inList = true, rating = 0, onRate, note = '', onSaveNote, onMark, onRewind, onClose, community = null }) {
  const [text, setText] = useState(note || '')
  const [saved, setSaved] = useState(null)
  useEffect(() => { setText(note || ''); setSaved(null) }, [note, season, episode])
  // The one measurement the v16 heatmap never had: is the community number looked
  // at? Logged once per sheet open that shows one (2026-09-20).
  useEffect(() => { if (community) logEvent('episode_quality_seen', { show: show?.id, s: season, e: episode, vote: community.vote }) }, [show?.id, season, episode]) // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = (text || '') !== (note || '')

  const save = async () => {
    let ok
    try { ok = await onSaveNote?.(text.trim()) } catch { ok = false }
    setSaved(ok === false ? 'fail' : 'ok'); setTimeout(() => setSaved(null), 1800)
  }

  return (
    <Sheet open onClose={onClose} label={`S${season} · E${episode}`} from="bottom" panelStyle={{ padding: '10px 20px calc(30px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ width: 36, height: 4, borderRadius: 999, background: 'var(--border)', margin: '0 auto 16px' }} />
      <div style={EYE}>S{season} · E{episode}{watched ? ' · watched' : isNext ? ' · next' : ''}</div>
      <div style={{ fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 500, color: 'var(--text-strong)', lineHeight: 1.15, marginTop: 4 }}>{name || `Episode ${episode}`}</div>
      {meta && <div style={{ fontSize: 12.5, color: 'var(--text-dim)', marginTop: 4 }}>{meta}</div>}
      {community && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>
          <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, border: '1.5px solid', borderColor: `color-mix(in srgb, var(--text-strong) ${Math.round(community.t * 100)}%, var(--border))`, background: community.standout ? 'var(--amber)' : `color-mix(in srgb, var(--text-strong) ${Math.round(community.t * 10)}%, transparent)` }} />
          <span><span style={{ color: community.standout ? 'var(--amber)' : 'var(--text-strong)' }}>{community.vote.toFixed(1)}</span> / 10 on TMDB · {community.count.toLocaleString()} votes{community.standout ? ' · a standout' : ''}</span>
        </div>
      )}

      {/* Rate — was a hand-rolled 5-button FilmReel picker (a different glyph, whole
          numbers only) instead of the same ReelRating control every other rating
          surface uses (Pulse, Home cards, reviews) — a real visual + precision
          mismatch Jason flagged (2026-09-18). ReelRating already supports tap AND
          drag-to-half-point, and never auto-clears on a re-tap of the same value
          (by design, see its own comment) — so clearing now happens by dragging to
          the very start rather than re-tapping, consistent with every other reel
          control in the app. */}
      <div style={{ marginTop: 22 }}>
        <div style={{ ...EYE, marginBottom: 10 }}>Rate it</div>
        <ReelRating value={rating} onChange={onRate} size={36} />
      </div>

      {/* Note */}
      {onSaveNote && <div style={{ marginTop: 22 }}>
        <div style={{ ...EYE, marginBottom: 10 }}>Your note</div>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder="Just for you. A line about this one."
          style={{ width: '100%', boxSizing: 'border-box', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '10px 12px', color: 'var(--text-strong)', fontSize: 14.5, fontFamily: 'var(--font)', resize: 'vertical', outline: 'none' }} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={save} disabled={!dirty} className="ui-hit" style={{ height: 34, padding: '0 14px', borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: dirty ? 'var(--amber)' : 'var(--bg-card)', color: dirty ? 'var(--amber-ink)' : 'var(--text-dim)', fontFamily: 'var(--font)', fontSize: 13, fontWeight: 600, cursor: dirty ? 'pointer' : 'default' }}>{saved === 'fail' ? 'Didn’t save' : saved ? 'Saved' : 'Save note'}</button>
        </div>
      </div>}

      {/* Mark / rewind */}
      {aired && (
        <div style={{ marginTop: 18, display: 'flex', gap: 10 }}>
          {watched ? (
            <button onClick={() => { onRewind?.(); onClose?.() }} className="ui-hit" style={{ flex: 1, height: 44, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <ClapboardOpen size={16} color="var(--amber)" />Rewind to here
            </button>
          ) : (
            <button onClick={() => { onMark?.(); onClose?.() }} className="ui-hit" style={{ flex: 1, height: 44, borderRadius: 'var(--radius-pill)', border: '1px solid var(--amber)', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <ClapboardClosed size={16} color="var(--amber-ink)" />{!inList ? 'Add to my list' : isNext ? 'Mark watched' : 'Mark watched through here'}
            </button>
          )}
        </div>
      )}
    </Sheet>
  )
}
